import {
  Text,
  Node as SlateNode,
  type Editor,
  type Element,
  type Path,
  type Descendant,
} from 'slate';
import DOMPurify from 'dompurify';
import AhaEditor from '../AhaEditor';
import SerializationPlugins from './serializationPlugins';

export interface MarkdownSerializerOptions {
  baseURL?: string;
}

export interface MarkdownSerializerContext {
  editor?: Editor;
  treeDepth?: number;
  preDepth?: number;
  // Stack of nested list types and current count for each list.
  listType: ('ul' | 'ol')[];
  listCounter: number[];
  // Table context for collecting cells and generating markdown tables
  tableData?: {
    rows: string[][];
    hasHeader?: boolean;
  };
}

// Convert Slate JSON to and from Markdown.
// * Serialize: Slate JSON -> Markdown
// * Deserialize: Markdown -> Slate JSON
class MarkdownSerializer {
  editor: Editor;
  context: MarkdownSerializerContext = { listType: [], listCounter: [] };
  options: MarkdownSerializerOptions = {};

  // An editor instance is necessary here so that we can know the difference
  // between inline and block elements.
  constructor(
    editor: Editor = MarkdownSerializer.createGenericEditor(),
    options: MarkdownSerializerOptions = {}
  ) {
    this.editor = editor;

    this.options = {
      baseURL:
        options.baseURL ||
        (typeof window !== 'undefined' ? window.location.origin : undefined),
    };
  }

  // Create an editor instance with all known plugins.
  static createGenericEditor(): Editor {
    const ahaEditor = new AhaEditor();
    ahaEditor.register(SerializationPlugins);

    return ahaEditor.editor;
  }

  serializeTextNode(node: Text, path: Path): string {
    let output = node.text;

    // Run the mark serializers.
    if (node.bold) {
      output = `**${output}**`;
    }
    if (node.italic) {
      output = `*${output}*`;
    }
    return output;
  }

  stripNewlines(str: string): string {
    return str.replace(/\n\n$/, '');
  }

  // NOTE: Securely strip HTML tags using DOMPurify to prevent XSS vulnerabilities.
  // Use KEEP_CONTENT: true to preserve text content inside tags while removing all HTML.
  stripTags(str: string): string {
    return DOMPurify.sanitize(str, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      USE_PROFILES: { html: false, svg: false },
      KEEP_CONTENT: true,
    });
  }

  indent(depth: number): string {
    let output = '';
    for (let i = 1; i < depth; i++) {
      output += '    ';
    }
    return output;
  }

  listItem(node: Element, children: string) {
    const listIndex = this.context.listType.length - 1;
    const output = `${this.indent(this.context.listType.length)}${
      this.context.listType[listIndex] === 'ol'
        ? `${this.context.listCounter[listIndex]++}.`
        : '-'
    } ${this.stripNewlines(children)}\n`;

    return output;
  }

  // Generate markdown table separator row with basic left alignment
  generateTableSeparator(columnCount: number): string {
    return (
      '|' +
      Array(columnCount)
        .fill('')
        .map(() => '----------')
        .join('|') +
      '|\n'
    );
  }

  // Convert collected table data to markdown table format
  serializeTable(hasHeader: boolean, rows: string[][]): string {
    if (rows.length === 0) return '';

    let output = '';

    // Generate table rows
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      output += '|' + row.join('|') + '|\n';

      // Add separator after header row
      if (hasHeader && i === 0) {
        output += this.generateTableSeparator(row.length);
      }
    }

    // If no header flag but we have rows, treat first row as data
    if (!hasHeader && rows.length > 0) {
      // For tables without headers, we still need a separator for valid markdown
      // We'll create a dummy header separator
      output = this.generateTableSeparator(rows[0].length) + output;
    }

    return output + '\n';
  }

  serializeElement(node: Descendant, path: Path): string {
    if (Text.isText(node)) {
      return this.serializeTextNode(node, path);
    }

    switch (node.type) {
      case 'ol_list':
        this.context.listType.push('ol');
        this.context.listCounter.push(1);
        break;
      case 'ul_list':
        this.context.listType.push('ul');
        this.context.listCounter.push(1);
        break;
      case 'table':
        this.context.tableData = {
          rows: [],
          hasHeader: (node as any).table_headerRow || false,
        };
        break;
      case 'table-row':
        if (this.context.tableData) {
          this.context.tableData.rows.push([]);
        }
        break;
    }

    let output: string = '';
    const childElements = Array.from(SlateNode.children(this.editor, path));

    for (let i = 0; i < childElements.length; i++) {
      const child = childElements[i];
      output = output.concat(this.serializeElement(child[0], child[1]));
    }

    switch (node.type) {
      case 'paragraph':
        output = `${output}\n\n`;
        break;
      case 'quote':
        output = `> ${output}\n\n`;
        break;
      case 'list_item':
        output = this.listItem(node, output);
        break;
      case 'code-snippet':
        output = `\`${output}\``;
        break;
      case 'h1':
        output = `# ${output}\n\n`;
        break;
      case 'h2':
        output = `## ${output}\n\n`;
        break;
      case 'h3':
        output = `### ${output}\n\n`;
        break;
      case 'h4':
        output = `#### ${output}\n\n`;
        break;
      case 'h5':
        output = `##### ${output}\n\n`;
        break;
      case 'h6':
        output = `###### ${output}\n\n`;
        break;
      case 'aha-reference':
        if (node.reference_num === node.reference_name) {
          output = node.reference_num;
        } else {
          output = `${node.reference_num} (${node.reference_name})`;
        }
        break;
      case 'table':
        if (this.context.tableData) {
          output = this.serializeTable(
            this.context.tableData.hasHeader ?? false,
            this.context.tableData.rows
          );
        }
        break;
      case 'table-row':
        // Table rows are handled via table data collection, return empty
        output = '';
        break;
      case 'table-cell':
        // Add cell content to current row
        if (this.context.tableData && this.context.tableData.rows.length > 0) {
          const currentRow =
            this.context.tableData.rows[this.context.tableData.rows.length - 1];
          // Clean up cell content - strip HTML tags, remove newlines and pipe characters that would break table format
          const cellContent = this.stripTags(output)
            .replace(/\n/g, ' ')
            .replace(/\|/g, '\\|')
            .trim();
          currentRow.push(` ${cellContent} `);
        }
        output = '';
        break;
    }

    switch (node.type) {
      case 'ol_list':
      case 'ul_list':
        this.context.listType.pop();
        this.context.listCounter.pop();
        break;
      case 'table':
        // Clean up table context after processing
        this.context.tableData = undefined;
        break;
    }

    return output;
  }

  serialize(options: MarkdownSerializerOptions = {}): string {
    this.options = { ...this.options, ...options };
    this.context = { editor: this.editor, listType: [], listCounter: [] };

    let output: string = '';
    for (const [child, childPath] of SlateNode.children(this.editor, [])) {
      output = output.concat(this.serializeElement(child, childPath));
    }

    return output;
  }

  deserialize(
    markdown: string | null,
    options: MarkdownSerializerOptions = {}
  ): Descendant[] {
    // Not implemented yet.
    return [];
  }

  static serializeFragment(fragment: Descendant[]): string {
    const editor = MarkdownSerializer.createGenericEditor();
    editor.children = fragment;
    const serializer = new MarkdownSerializer(editor);
    return serializer.serialize();
  }
}

export default MarkdownSerializer;
